import React from 'react'

import { Checkbox, Group, Menu, Text, Tooltip } from '@mantine/core'
import { observer } from 'mobx-react-lite'

import { jumpToPage } from '#choo-app/lib/editor/commands'
import * as commands from '#choo-app/lib/editor/plugins/locked-pages'
import { ExplorerView } from '#components/ExplorerView'
import { FaIcon } from '#components/FaIcon'
import { InlineTextForm } from '#components/InlineTextForm'
import { EmptyPageFinder } from '#components/PagesExplorer/EmptyPageFinder'
import {
  SelectableList,
  type SelectableListItem,
} from '#components/SelectableList'
import { useEditor } from '#hooks'
import { ILoadedScript } from '#state'
import { pluralize } from '#util'

import { TitlePageSection } from './TitlePageSection'

import styles from './PagesExplorer.module.scss'

interface PageItem extends SelectableListItem {
  id: string
  pageNumber: number
  isEmptyPage: boolean
  pageAlias?: string
  isLocked: boolean
  isCurrentPage: boolean
}

interface PageRowProps {
  pageItem: PageItem
  isEditing: boolean
  showCheckbox: boolean
  onPageRename(pageNumber: number, newName: string): void
  onCancelEdit(): void
}

const PageRow = ({
  pageItem,
  isEditing,
  showCheckbox,
  onPageRename,
  onCancelEdit,
}: PageRowProps) => {
  const { pageNumber, isEmptyPage, pageAlias, isLocked, isCurrentPage } =
    pageItem

  return (
    <Group
      key={pageNumber}
      px="sm"
      gap="xs"
      wrap="nowrap"
      className={styles.pagelock_row}
      data-selected={isCurrentPage}
    >
      {showCheckbox && <Checkbox size="sm" value={pageNumber.toString()} />}
      {isEditing ? (
        <Group gap="xs" flex="1" wrap="nowrap">
          <Text py="sm">Page </Text>
          <InlineTextForm
            defaultValue={pageAlias || pageNumber.toString()}
            onSubmit={(value) => onPageRename(pageNumber, value)}
            onCancel={onCancelEdit}
            size="xs"
            autoComplete="off"
            flex="1"
          />
        </Group>
      ) : (
        <>
          <Text
            flex="1"
            py="sm"
            style={{
              WebkitUserSelect: 'none',
              userSelect: 'none',
            }}
          >
            Page {pageAlias ?? pageNumber}
          </Text>
          {isEmptyPage && (
            <Tooltip label="Empty page">
              <FaIcon size="15" icon="fa-circle-exclamation" c="blue.4" />
            </Tooltip>
          )}
          {isLocked && (
            <FaIcon icon="fa-lock" size="sm" style={{ opacity: 0.5 }} />
          )}
        </>
      )}
    </Group>
  )
}

interface PagesExplorerInnerProps {
  script: ILoadedScript
  editable: boolean
}

const PagesExplorerInner = observer<PagesExplorerInnerProps>(
  function PagesExplorerInner(props) {
    const { script } = props
    const { pageCount } = script
    const editor = useEditor()
    const [editingPage, setEditingPage] = React.useState<number | null>(null)

    if (pageCount === null) return null

    const hasEmptyPages = script.emptyPageCount > 0
    const countText = `${pageCount} ${pluralize(pageCount, 'page')}`
    const isInteractive = props.editable && script.hasLockedPages
    const pageItems = script.pageItems

    function handlePageRename(pageNumber: number, newName: string) {
      const trimmedName = newName.trim()
      if (trimmedName) {
        editor.invoke(commands.setPageName(pageNumber, trimmedName))
      } else {
        editor.invoke(commands.clearPageName(pageNumber))
      }
      setEditingPage(null)
    }

    const renderContextMenu = (pageItem: PageItem) => (
      <>
        <Menu.Item
          onClick={() => {
            pageItem.isLocked
              ? editor.invoke(commands.unlockPages([pageItem.pageNumber]))
              : editor.invoke(commands.lockPages([pageItem.pageNumber]))

            script.trackEvent(
              pageItem.isLocked ? 'LOCK_SINGLE_PAGE' : 'UNLOCK_SINGLE_PAGE',
            )
          }}
        >
          {pageItem.isLocked ? 'Unlock' : 'Lock'} page
        </Menu.Item>
        <Menu.Item
          onClick={() => setEditingPage(pageItem.pageNumber)}
          disabled={!pageItem.isLocked}
        >
          Renumber page
        </Menu.Item>
        <Menu.Item
          onClick={() =>
            editor.invoke(commands.deleteEmptyPage(pageItem.pageNumber))
          }
          disabled={!pageItem.isEmptyPage}
        >
          Delete page
        </Menu.Item>
      </>
    )

    const renderBulkActionsMenu = (
      selectedItems: PageItem[],
      allItems: PageItem[],
    ) => {
      const selectedPageNumbers = selectedItems.map((item) => item.pageNumber)
      const hasUnlockedPages = allItems.some((item) => !item.isLocked)
      const hasLockedPages = allItems.some((item) => item.isLocked)
      const hasSelection = selectedItems.length > 0

      return (
        <>
          <Menu.Item
            onClick={() => {
              editor.invoke(commands.lockAllPages)
              script.trackEvent('LOCK_ALL_PAGES', {
                totalPageCount: script.totalLockedPages,
              })
            }}
            disabled={!hasUnlockedPages}
          >
            Lock all pages
          </Menu.Item>
          <Menu.Item
            onClick={() => {
              editor.invoke(commands.unlockAllPages)
              script.trackEvent('UNLOCK_ALL_PAGES', {
                totalPageCount: script.totalUnlockedPages,
              })
            }}
            disabled={!hasLockedPages}
          >
            Unlock all pages
          </Menu.Item>
          <Menu.Divider />
          <Menu.Item
            onClick={() => {
              editor.invoke(commands.lockPages(selectedPageNumbers))
              script.trackEvent('LOCK_SELECTED_PAGES', {
                pageCount: selectedPageNumbers.length,
                totalPageCount: script.totalLockedPages,
              })
            }}
            disabled={!hasSelection}
          >
            Lock selected pages
          </Menu.Item>
          <Menu.Item
            onClick={() => {
              editor.invoke(commands.unlockPages(selectedPageNumbers))
              script.trackEvent('UNLOCK_SELECTED_PAGES', {
                pageCount: selectedPageNumbers.length,
                totalPageCount: script.totalUnlockedPages,
              })
            }}
            disabled={!hasSelection}
          >
            Unlock selected pages
          </Menu.Item>
        </>
      )
    }

    const headerActions = props.editable && (
      <Group gap={4} wrap="nowrap">
        <FaIcon size="sm" icon="fa-lock" />
        <FaIcon size="12" icon="fa-caret-down" />
      </Group>
    )

    return (
      <ExplorerView>
        <TitlePageSection script={script} />
        <SelectableList
          items={pageItems}
          selectionMode={isInteractive ? 'multiple' : 'none'}
          onAction={(key) => editor.invoke(jumpToPage(parseInt(key, 10) - 1))}
          title={countText}
          headerActions={headerActions}
          renderBulkActionsMenu={
            props.editable ? renderBulkActionsMenu : undefined
          }
          renderContextMenu={isInteractive ? renderContextMenu : undefined}
        >
          {(pageItem) => (
            <PageRow
              pageItem={pageItem}
              isEditing={editingPage === pageItem.pageNumber}
              showCheckbox={isInteractive}
              onPageRename={handlePageRename}
              onCancelEdit={() => setEditingPage(null)}
            />
          )}
        </SelectableList>
        {hasEmptyPages && <EmptyPageFinder count={script.emptyPageCount} />}
      </ExplorerView>
    )
  },
)

export const PagesExplorer = (props: PagesExplorerInnerProps) => {
  return <PagesExplorerInner {...props} />
}
